import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'src/components/ui/Features/Modal/Modal';
import { useChallenge } from 'src/hook/data/useChallenge';
import { useLearning } from 'src/hook/data/useLearning';
import { WordListType } from 'src/model/enum/enums';
import { WordList } from 'src/model/WordList';
import { IconLocation } from 'src/utils/URLUtils';
import styles from './WordListsModal.module.scss';

interface WordListsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const WordListsModal = ({ isOpen, onClose }: WordListsModalProps) => {
    const { t } = useTranslation();
    const { learningInfo } = useLearning();
    const { selectedLists, setSelectedLists, setIsFiltered } = useChallenge();
    const [localSelectedLists, setLocalSelectedLists] = useState<WordList[]>([]);

    const getFilteredListLength = useCallback((list: WordList) => {
        if (!list || !learningInfo?.ReviewWordIds) return 0;
        const reviewWordBaseIds = learningInfo.ReviewWordIds
            .map(id => parseInt(id.split(':')[0]));

        if (list.Type === WordListType.All) {
            return reviewWordBaseIds.length || 0;

        }

        if (list.Type === WordListType.Custom && list.CustomListData) {
            const customListWordIds = list.CustomListData
                .map(item => item.WordId);
            return reviewWordBaseIds.filter(id =>
                customListWordIds.includes(id)
            ).length || 0;
        }

        if (list.Type === WordListType.Subject && list.WordListData) {
            return reviewWordBaseIds.filter(id =>
                list.WordListData.includes(id)
            ).length || 0;
        }

        return 0;
    }, [learningInfo?.ReviewWordIds]);

    const availableLists = useMemo(() =>
        learningInfo?.WordLists?.filter(list => getFilteredListLength(list) > 0) || [],
        [learningInfo?.WordLists, getFilteredListLength]
    );

    useEffect(() => {
        if (isOpen && selectedLists.length === 0 && learningInfo?.WordLists) {
            const listsWithWords = learningInfo.WordLists.filter(list => {
                const reviewWordBaseIds = learningInfo.ReviewWordIds
                    .map(id => parseInt(id.split(':')[0]));

                if (list.Type === WordListType.All) {
                    return reviewWordBaseIds.length > 0;
                }
                if (list.Type === WordListType.Custom && list.CustomListData) {
                    return list.CustomListData
                        .some(item => reviewWordBaseIds.includes(item.WordId));
                }
                if (list.Type === WordListType.Subject && list.WordListData) {
                    return list.WordListData
                        .some(id => reviewWordBaseIds.includes(id));
                }
                return false;
            });
            setLocalSelectedLists(listsWithWords);
        } else if (isOpen) {
            setLocalSelectedLists(selectedLists);
        }
    }, [isOpen, learningInfo, selectedLists]);

    const isAllSelected = useMemo(() => {
        if (!availableLists.length) return false;
        return availableLists.every(list =>
            localSelectedLists.some(selected => selected.ID === list.ID)
        );
    }, [availableLists, localSelectedLists]);

    const handleCheckboxChange = useCallback((list: WordList) => {
        setLocalSelectedLists(prev => {
            if (prev.some(selected => selected.ID === list.ID)) {
                return prev.filter(selected => selected.ID !== list.ID);
            }
            return [...prev, list];
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setLocalSelectedLists(isAllSelected ? [] : availableLists);
    }, [availableLists, isAllSelected]);

    const handleApply = useCallback(() => {
        setSelectedLists(localSelectedLists);
        setIsFiltered(true);
        onClose();
    }, [localSelectedLists, setSelectedLists, setIsFiltered, onClose]);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className={styles.wordListsModal}>
                <div className={styles.modalHeader}>
                    <h1>{t('Required subjects')}</h1>
                    <img src={IconLocation('x')} alt={t('close')} onClick={onClose} />
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.checkboxRow}>
                        <input
                            type="checkbox"
                            id="selectAll"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                        />
                        <label htmlFor="selectAll" className={styles.listTitle}>{t('Select All')}</label>
                    </div>
                    {availableLists.map((list, index) => (
                        <div key={list.ID} className={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                id={list.ID}
                                checked={localSelectedLists.some(
                                    selected => selected.ID === list.ID
                                )}
                                onChange={() => handleCheckboxChange(list)}
                            />
                            <label htmlFor={list.ID} className={styles.listTitle}>
                                {`${list.Title} (${getFilteredListLength(list)})`}
                            </label>
                        </div>
                    ))}
                </div>
                <hr />
                <button
                    className={styles.applyButton}
                    disabled={localSelectedLists.length === 0}
                    onClick={handleApply}
                >
                    {t('Apply')}
                </button>
            </div>
        </Modal>
    );
};